import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Truck, CheckCircle, XCircle, Archive } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData, DirtyCell } from '@/components/admin/DataTable';
import { useUIStore } from '@/stores/ui-store';

interface Order {
  id: string;
  order_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  total: number;
  created_at: string;
  item_count: number;
  tracking_number: string | null;
  tracking_carrier: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_archived?: boolean;
  order_source?: string;
  tags?: string[] | null;
}

interface OrdersResponse {
  orders: Order[];
  pagination: PaginationData;
}

type OrderStatus = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

const getStatusBadgeVariant = (status: Order['status']): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
  switch (status) {
    case 'pending': return 'yellow';
    case 'processing':
    case 'shipped': return 'blue';
    case 'delivered': return 'green';
    case 'cancelled':
    case 'refunded': return 'red';
    default: return 'gray';
  }
};

const getPaymentStatusBadgeVariant = (status: Order['payment_status']): 'green' | 'yellow' | 'red' | 'gray' => {
  switch (status) {
    case 'paid': return 'green';
    case 'pending': return 'yellow';
    case 'failed':
    case 'refunded': return 'red';
    default: return 'gray';
  }
};

const columns: ColumnDef<Order>[] = [
  {
    id: 'order_number',
    header: 'Order',
    accessorKey: 'order_number',
    sortable: true,
    hideable: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Link
          to={`/admin/orders/${row.id}`}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          #{row.order_number}
        </Link>
        {row.is_archived && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
            <Archive className="h-3 w-3" /> Archived
          </span>
        )}
        {row.tags && row.tags.length > 0 && (
          <span className="text-xs text-gray-400">{row.tags.join(', ')}</span>
        )}
      </div>
    ),
  },
  {
    id: 'customer',
    header: 'Customer',
    accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email,
    sortable: true,
    cell: ({ row }) => (
      <div>
        <div className="text-sm text-gray-900">
          {row.first_name || row.last_name ? `${row.first_name || ''} ${row.last_name || ''}`.trim() : 'Guest'}
        </div>
        <div className="text-sm text-gray-500">{row.email}</div>
      </div>
    ),
  },
  {
    id: 'created_at',
    header: 'Date',
    accessorKey: 'created_at',
    sortable: true,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">{formatDate(row.created_at)}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Pending', value: 'pending' },
      { label: 'Processing', value: 'processing' },
      { label: 'Shipped', value: 'shipped' },
      { label: 'Delivered', value: 'delivered' },
      { label: 'Cancelled', value: 'cancelled' },
    ],
    cell: ({ row }) => (
      <Badge variant={getStatusBadgeVariant(row.status)}>
        {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
      </Badge>
    ),
  },
  {
    id: 'payment_status',
    header: 'Payment',
    accessorKey: 'payment_status',
    cell: ({ row }) => (
      <Badge variant={getPaymentStatusBadgeVariant(row.payment_status)}>
        {row.payment_status.charAt(0).toUpperCase() + row.payment_status.slice(1)}
      </Badge>
    ),
  },
  {
    id: 'total',
    header: 'Total',
    accessorKey: 'total',
    sortable: true,
    align: 'right',
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">{formatCurrency(row.total)}</span>
    ),
  },
  {
    id: 'item_count',
    header: 'Items',
    accessorKey: 'item_count',
    align: 'right',
    hideable: true,
    defaultVisible: true,
  },
  {
    id: 'tracking_number',
    header: 'Tracking',
    accessorKey: 'tracking_number',
    editable: true,
    editType: 'text',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {row.tracking_number || '-'}
      </span>
    ),
  },
];

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState, markDirty } = useDataTableState({
    storageKey: 'admin-orders',
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultLimit: 25,
    columns,
    filterKeys: ['status'],
  });

  const statusFilter = (state.filters.status || 'all') as OrderStatus;

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (state.search) params.set('q', state.search);
        if (state.sort) params.set('sort', state.sort);
        params.set('order', state.order);
        params.set('is_archived', showArchived ? 'all' : 'false');

        const response = await api.get<OrdersResponse>(`/admin/orders?${params.toString()}`);
        setOrders(response.orders);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [state.page, state.limit, state.search, statusFilter, state.sort, state.order, showArchived]);

  const handleStatusFilter = (status: OrderStatus) => {
    updateState({ filters: { ...state.filters, status: status === 'all' ? '' : status } });
  };

  const handleBatchSave = useCallback(async (changes: DirtyCell[]) => {
    const byRow = new Map<string, Record<string, unknown>>();
    for (const cell of changes) {
      if (!byRow.has(cell.rowId)) byRow.set(cell.rowId, {});
      byRow.get(cell.rowId)![cell.columnId] = cell.value;
    }

    const errors: string[] = [];
    for (const [rowId, fields] of byRow) {
      try {
        // Use the existing PUT /status endpoint for status changes
        if (fields.status) {
          await api.put(`/admin/orders/${rowId}/status`, { status: fields.status, ...fields });
        } else {
          await api.put(`/admin/orders/${rowId}/status`, fields);
        }
        setOrders((prev) =>
          prev.map((o) => (o.id === rowId ? { ...o, ...fields } : o))
        );
      } catch (err: any) {
        errors.push(err.message || `Failed to update order ${rowId}`);
      }
    }

    if (errors.length > 0) {
      addToast(`${errors.length} update(s) failed`, 'error');
      throw new Error('Some updates failed');
    }
    addToast(`${byRow.size} order(s) updated`, 'success');
  }, [addToast]);

  const handleDirtyChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      markDirty(rowId, columnId, value, originalValue);
    },
    [markDirty]
  );

  const bulkActions: BulkAction<Order>[] = [
    {
      id: 'mark-shipped',
      label: 'Mark Shipped',
      icon: <Truck className="w-4 h-4 mr-1" />,
      variant: 'primary',
      confirmMessage: 'Mark the selected orders as shipped?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.put(`/admin/orders/${row.id}/status`, { status: 'shipped' });
        }
        setOrders((prev) =>
          prev.map((o) => (rows.some((r) => r.id === o.id) ? { ...o, status: 'shipped' as const } : o))
        );
        addToast(`${rows.length} order(s) marked as shipped`, 'success');
      },
    },
    {
      id: 'mark-delivered',
      label: 'Mark Delivered',
      icon: <CheckCircle className="w-4 h-4 mr-1" />,
      variant: 'primary',
      confirmMessage: 'Mark the selected orders as delivered?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.put(`/admin/orders/${row.id}/status`, { status: 'delivered' });
        }
        setOrders((prev) =>
          prev.map((o) => (rows.some((r) => r.id === o.id) ? { ...o, status: 'delivered' as const } : o))
        );
        addToast(`${rows.length} order(s) marked as delivered`, 'success');
      },
    },
    {
      id: 'cancel',
      label: 'Cancel',
      icon: <XCircle className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Cancel the selected orders? This cannot be undone.',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.put(`/admin/orders/${row.id}/status`, { status: 'cancelled' });
        }
        setOrders((prev) =>
          prev.map((o) => (rows.some((r) => r.id === o.id) ? { ...o, status: 'cancelled' as const } : o))
        );
        addToast(`${rows.length} order(s) cancelled`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
        <p className="mt-1 text-gray-600">Manage and track customer orders</p>
      </div>

      {/* Status Tabs */}
      <Card padding="md">
        <div className="flex items-center justify-between">
          <div className="flex gap-2 border-b border-gray-200">
            {(['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'] as OrderStatus[]).map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={cn(
                  'px-4 py-2 font-medium text-sm border-b-2 transition-colors',
                  statusFilter === status
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                )}
                aria-current={statusFilter === status ? 'page' : undefined}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show Archived
          </label>
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={orders}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        onBatchSave={handleBatchSave}
        onDirtyChange={handleDirtyChange}
        bulkActions={bulkActions}
        searchPlaceholder="Search by order number or customer email..."
      />
    </div>
  );
}
